/*
 * @Author: Zhouqi
 * @Date: 2022-03-21 11:07:10
 * @LastEditors: Zhouqi
 * @LastEditTime: 2023-01-09 15:57:32
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react'
import type { ReactNode } from 'react'
import { Pagination, Image } from 'antd'
import Icon from '@ant-design/icons'
import styles from './index.module.scss'
import DocumentSvg from '../../assets/svg/text.svg'
import RadioSvg from '../../assets/svg/radio.svg'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { getCourseList, getTagList } from '../../api/allcouse'
import axios from 'axios'
import { LocalImageLoader } from '../../loader/loader'
import NoData from '../../assets/images/noData.png'
import NextImage from 'next/image'
import Head from 'next/head'
import { Emiter } from '../../utils/Emiter.ts'
import { getUrlParam } from '../../utils/util'

type itemRenderType = 'page' | 'prev' | 'next' | 'jump-prev' | 'jump-next'
type RoleOptions = {
  roleId: string
  roleName: string
}
type TagType = 'tagList' | 'courseList'

const SORT_ITEMS = [
  { title: '最新', id: 1 },
  { title: '最热', id: 2 },
  { title: '推荐', id: 0 },
]
const BASE_MODULE = {
  moduleId: '',
  moduleName: '全部',
}
const env = process.env.NODE_ENV

function itemRender(
  current: number,
  type: itemRenderType,
  originalElement: ReactNode
): ReactNode {
  if (type === 'prev') {
    return <a style={{ userSelect: 'none' }}>上一页</a>
  }

  if (type === 'next') {
    return <a style={{ userSelect: 'none' }}>下一页</a>
  }

  return originalElement
}

/**
 * @author: Zhouqi
 * @description: 获取返回的全部角色数据
 * @param {Array} moduleData 模块数据
 * @param {number} moduleId 选中的模块id
 * @return {Array} 所有角色数据
 */
function getRoleList(moduleData: Record<string, any>[], moduleId: string) {
  const baseRole: RoleOptions = {
    roleId: '',
    roleName: '不限',
  }
  let result: Array<RoleOptions> = []
  if (moduleId !== '') {
    const moduleItem = moduleData.find((module) => module.moduleId === moduleId)
    result = [].concat(moduleItem && moduleItem.roleList)
  } else {
    const roleIds = new Set()
    moduleData.forEach((module) => {
      const { roleList } = module
      Array.isArray(roleList) &&
        roleList.forEach((role) => {
          const { roleId, roleName } = role
          if (!roleIds.has(roleId)) {
            result.push({
              roleId,
              roleName,
            })
            roleIds.add(roleId)
          }
        })
    })
  }
  result.unshift(baseRole)
  return result
}

const AllCourse: NextPage = (props: Record<string, any>) => {
  const [moduleActiveIndex, setModuleActiveIndex] = useState(0),
    [roleActiveIndex, setRoleActiveIndex] = useState(0),
    [sortActiveIndex, setSortActiveIndex] = useState(1),
    [moduleId, setModuleId] = useState(''),
    [roleId, setRoleId] = useState(''),
    [userRoles, setUserRoles]: [Array<RoleOptions>, Function] = useState([]),
    [tagList, setTagList] = useState(props.tagList || []),
    [courseList, setCourseList] = useState(props.courseList || []),
    [pageInfo, setPageInfo] = useState({
      pageIndex: 0,
      total: 0,
      pageSize: 20,
    }),
    [showMoreRoleTag, setShowMoreRoleTag] = useState(false),
    router = useRouter(),
    [searchValue, setSearchValue] = useState(
      router.asPath.split('?')[1]
        ? getUrlParam(router.asPath.split('?')[1], 'searchValue')
        : ''
    )

  const roleTag = useRef('roleTagRef')
  // 处理请求参数
  const getParamsData = () => {
    const tagIdArray = []
    moduleId && tagIdArray.push(moduleId)
    roleId && tagIdArray.push(roleId)
    return {
      tagIdArray: tagIdArray.join(','),
      pageIndex: pageInfo.pageIndex,
      sort: sortActiveIndex,
      courseTitle: searchValue,
    }
  }
  // 切换模块
  const changeModule = (module: Record<string, any>, activeIndex: number) => {
    const { moduleId, roleList } = module
    setModuleActiveIndex(activeIndex)
    setModuleId(moduleId)

    setPageInfo({
      pageIndex: 0,
      total: pageInfo.total,
      pageSize: pageInfo.pageSize,
    })

    // 之前选中的是不限，则不管如何切换模块都是不限
    if (roleId === '') {
      setRoleActiveIndex(0)
      return
    }

    let roleActiveIndex = 0

    // 选中的是全部，此时可以找到之前选中的角色
    if (moduleId === '') {
      roleActiveIndex = getRoleList(tagList, moduleId).findIndex(
        (role: RoleOptions) => role.roleId === roleId
      )
      setRoleActiveIndex(roleActiveIndex)
      return
    }

    // 如果选中的是其它模块，且之前选中的角色存在这个模块中
    roleActiveIndex = roleList.findIndex(
      (role: RoleOptions) => role.roleId === roleId
    )

    if (roleActiveIndex === -1) {
      setRoleActiveIndex(0)
      setRoleId('')
      return
    }

    setRoleActiveIndex(roleActiveIndex + 1)
  }

  // 切换角色
  const changeRole = (role: RoleOptions, activeIndex: number) => {
    const { roleId } = role
    setRoleActiveIndex(activeIndex)
    setRoleId(roleId)
    setPageInfo({
      pageIndex: 0,
      total: pageInfo.total,
      pageSize: pageInfo.pageSize,
    })
  }

  // 获取课程数据
  const getCourse = async (params: any) => {
    const { result } = (await getCourseList(params)) as Record<string, any>
    const { data = [], pageInfo = {} } = result
    setPageInfo({
      pageIndex: pageInfo.pageIndex,
      total: pageInfo.total,
      pageSize: pageInfo.pageSize,
    })
    setCourseList(data)
  }

  // 获取标记数据
  const getTag = async () => {
    const { result } = (await getTagList()) as Record<string, any>
    const data = result?.data || []
    data.unshift(BASE_MODULE)
    setTagList(data)
  }

  const changeRoleTagStyle = ({
    maxHeight,
    bgColor,
    zIndex,
    position,
    boxShadow,
    transition,
  }: Record<string, any>) => {
    const roleDom = roleTag.current as any as HTMLElement
    roleDom.style.position = position
    roleDom.style.zIndex = zIndex
    roleDom.style.boxShadow = boxShadow
    roleDom.style.transition = transition
    roleDom.style.backgroundColor = bgColor
    roleDom.style.maxHeight = maxHeight
  }

  const showRoleTagShadow = () => {
    const roleDom = roleTag.current as any as HTMLElement
    const liDomLists = roleDom.children[1].children
    const liLists = Array.from(liDomLists)
    const len = liLists.length
    let width = 0
    for (let i = 0; i < len && width <= 1200; i++) {
      const li = liLists[i] as HTMLElement
      width += li.offsetWidth
    }
    const flag = width > 1200
    setShowMoreRoleTag(flag)
    if (!flag) {
      changeRoleTagStyle({
        maxHeight: '38px',
        bgColor: '#fff',
        position: 'static',
        boxShadow: 'rgb(95 101 105 / 0%) 0px 12px 20px 0px',
      })
      setBlockStyle({ height: 0, display: 'none' })
    }
  }

  const search = (value: string) => {
    if (value !== searchValue) {
      setSearchValue(value)
      setPageInfo({
        pageIndex: 0,
        total: pageInfo.total,
        pageSize: pageInfo.pageSize,
      })
    }
  }

  useEffect(() => {
    Emiter.on('search', search)
    return () => {
      Emiter.off('search', search)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue])

  // 模块id和tagList变化时重新生成角色数据
  useEffect(() => {
    const roles = getRoleList(tagList, moduleId)
    setUserRoles(roles)
  }, [moduleId, tagList])

  // 角色权限/课程模块/课程标记改变时获取课程数据
  useEffect(() => {
    getCourse(getParamsData())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId, moduleId, sortActiveIndex, pageInfo.pageIndex, searchValue])

  useEffect(() => {
    getTag()
  }, [])

  useLayoutEffect(() => {
    showRoleTagShadow()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRoles])

  const [blockStyle, setBlockStyle] = useState({ height: 0, display: 'none' })

  return (
    <>
      <Head>
        <title>
          吉客云云课堂_软件培训_软件教程_使用教程_培训课程|吉客云官网
        </title>
        <meta
          name="keyword"
          content="吉客云云课堂,吉客云软件培训,吉客云软件教程,吉客云使用指南"
        />
        <meta
          name="description"
          content="云课堂通过线上直播公开课、线下专题培训、图文视频结合的形式详细讲解了吉客云软件各功能模块的场景使用、用户角色及操作流程,帮助初学者快速掌握吉客云。"
        />
      </Head>
      <header className={styles.header}>
        <div className={styles.filter}>
          <div className={styles.direction}>
            <p>功能模块：</p>
            <ul className={styles.items}>
              {tagList.map((item: Record<string, any>, index: number) => (
                <li
                  className={[
                    styles.item,
                    moduleActiveIndex === index ? styles.active : null,
                  ].join(' ')}
                  key={item.moduleId}
                  onClick={() => {
                    changeModule(item, index)
                  }}>
                  {item.moduleName}
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.direction} ref={roleTag as any}>
            <p> 用户角色：</p>
            <ul
              className={styles.items}
              onMouseEnter={(e) => {
                if (!showMoreRoleTag) return
                changeRoleTagStyle({
                  transition: 'max-height 1s ease',
                  maxHeight: '500px',
                  bgColor: '#f5f7fa',
                  zIndex: '30',
                  position: 'absolute',
                  boxShadow: 'rgb(95 101 105 / 10%) 0px 12px 20px 0px',
                })
                setBlockStyle({ height: 48, display: 'block' })
              }}
              onMouseLeave={(e) => {
                if (!showMoreRoleTag) return
                changeRoleTagStyle({
                  maxHeight: '38px',
                  bgColor: '#fff',
                  position: 'static',
                  boxShadow: 'rgb(95 101 105 / 0%) 0px 12px 20px 0px',
                })
                setBlockStyle({ height: 0, display: 'none' })
              }}>
              {userRoles.map((role: RoleOptions, index: number) => (
                <li
                  className={[
                    styles.item,
                    roleActiveIndex === index ? styles.active : null,
                  ].join(' ')}
                  key={role.roleId}
                  onClick={() => {
                    changeRole(role, index)
                  }}>
                  {role.roleName}
                </li>
              ))}
            </ul>
          </div>
          <div style={blockStyle}></div>
        </div>
      </header>
      <main className={styles.main} id="main">
        <div className={styles.courseContainer}>
          <div className={styles.sortItems}>
            {SORT_ITEMS.map((item, index) => (
              <span
                className={[
                  styles.sortItem,
                  sortActiveIndex === item.id ? styles.sortActive : null,
                ].join(' ')}
                key={item.id}
                onClick={() => {
                  setSortActiveIndex(item.id)
                  setPageInfo({
                    pageIndex: 0,
                    total: pageInfo.total,
                    pageSize: pageInfo.pageSize,
                  })
                }}>
                {item.title}
              </span>
            ))}
          </div>
          {courseList.length ? (
            <div className={styles.courseList}>
              {courseList.map((course: Record<string, any>) => {
                return (
                  <div
                    className={styles.courseListContainer}
                    key={course.courseCode}>
                    {course.hasDocument && (
                      <Icon
                        component={() => (
                          <DocumentSvg width="3em" height="2em" />
                        )}
                        style={{
                          position: 'absolute',
                          right: course.hasVideo ? 50 : 10,
                          top: -5,
                          zIndex: 10,
                        }}
                      />
                    )}
                    {course.hasVideo && (
                      <Icon
                        component={() => <RadioSvg width="3em" height="2em" />}
                        style={{
                          position: 'absolute',
                          right: 10,
                          top: -5,
                          zIndex: 10,
                        }}
                      />
                    )}
                    <div
                      className={styles.courseItem}
                      onClick={() => {
                        router.push({
                          pathname: `/allcourse/detail${
                            env === 'production' ? '.html' : ''
                          }`,
                          query: {
                            courseCode: course.courseCode,
                            moduleActiveIndex,
                          },
                        })
                      }}>
                      <Image
                        preview={false}
                        src={course.courseCover}
                        height={179}
                        width="100%"
                        alt="课程"
                      />
                      <div className={styles.courseDetail}>
                        <p
                          title={course.courseTitle}
                          className={styles.detailTitle}>
                          {course.courseTitle}
                        </p>
                        {/* <span className={styles.userCount}>
                          学习：{course.viewCount || 0}
                        </span> */}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div style={{ marginTop: 60, width: '100%', textAlign: 'center' }}>
              <NextImage
                unoptimized
                loader={LocalImageLoader}
                src={NoData}
                alt="暂无相关课程"
              />
              <p style={{ marginTop: 25, color: '#666' }}>暂无相关课程</p>
            </div>
          )}
        </div>
        {courseList.length ? (
          <div className={styles.pagination}>
            <Pagination
              itemRender={itemRender}
              total={pageInfo.total}
              style={{ float: 'right' }}
              current={pageInfo.pageIndex + 1}
              pageSize={pageInfo.pageSize}
              showSizeChanger={false}
              onChange={(page) => {
                setPageInfo({
                  pageIndex: page - 1,
                  total: pageInfo.total,
                  pageSize: pageInfo.pageSize,
                })
              }}
            />
          </div>
        ) : null}
      </main>
    </>
  )
}

/**
 * @author: Zhouqi
 * @description: 获取在线课程标记数据和课程数据
 * @param {function} callback 获取到数据后执行的回调函数
 * @return {object} 数据
 */
async function getCoursePageData(callback?: Function) {
  try {
    const [courseListData, tagListData] = (await axios.all([
      getCourseList(),
      getTagList(),
    ])) as Array<Record<string, any>>

    const tagList = tagListData?.result?.data || []
    const courseList = courseListData?.result?.data || []

    tagList.unshift(BASE_MODULE)

    const result = {
      tagList,
      courseList,
    }

    if (callback) {
      callback(result)
      return
    }

    return result
  } catch (error) {
    const errorResult = {
      tagList: [BASE_MODULE],
      courseList: [],
    }
    if (callback) {
      callback(errorResult)
      return
    }
    return errorResult
  }
}

export async function getStaticProps() {
  const resultData = (await getCoursePageData()) as Record<TagType, Array<any>>
  return {
    props: resultData,
  }
}

export default AllCourse
